"use client";

import TableSortingIcon from "@/components/common/TableSortingIcon";
import { Edit, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { ButtonTable, ButtonIcon } from "@/components/common/StandardButton";
import { Checkbox } from "@/components/ui/checkbox";
import { Coupon } from "@/types/coupon";
import { ColumnDef } from "@tanstack/react-table";
import { format } from "date-fns";
import CouponStatusCell from "./CouponStatusCell";

type CouponColumnsProps = {
  onRowClick?: (rowData: Coupon) => void;
};

export const columns = ({
  onRowClick,
}: CouponColumnsProps): ColumnDef<Coupon>[] => [
  {
    id: "select",
    header: ({ table }) => (
      <Checkbox
        checked={
          table.getIsAllPageRowsSelected() ||
          (table.getIsSomePageRowsSelected() && "indeterminate")
        }
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label="Select all"
        className="!bg-transparent border-foreground/50 text-foreground data-[state=checked]:text-foreground data-[state=checked]:bg-primary"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label="Select row"
        className="!bg-transparent border-foreground/50 text-foreground data-[state=checked]:text-foreground data-[state=checked]:bg-primary"
      />
    ),
  },
  {
    accessorKey: "title",
    header: ({ column }) => {
      return (
        <ButtonTable
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="w-full flex p-0 items-center justify-between hover:!bg-transparent hover:text-inherit text-foreground font-medium"
        >
          TITLE
          <TableSortingIcon />
        </ButtonTable>
      );
    },
    cell: ({ row }) => (
      <ButtonTable
        onClick={() => onRowClick?.(row.original)}
        className="p-0 text-foreground hover:text-primary transition-colors text-left text-sm max-w-[140px] pl-2 !bg-transparent underline"
      >
        {row.getValue("title")}
      </ButtonTable>
    ),
  },
  {
    accessorKey: "created_at",
    header: ({ column }) => {
      return (
        <ButtonTable
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="w-full flex p-0 items-center justify-between hover:!bg-transparent hover:text-inherit text-foreground font-medium"
        >
          CREATED DATE
          <TableSortingIcon />
        </ButtonTable>
      );
    },
    cell: ({ row }) => {
      const date = new Date(row.getValue("created_at"));
      const formattedDate = format(date, "EEE MMM dd yyyy");
      const formattedTime = format(date, "h:mm a");
      return (
        <div className="text-foreground">
          <h3 className="font-medium text-sm">{formattedDate}</h3>
          <span className="text-muted-foreground text-xs">{formattedTime}</span>
        </div>
      );
    },
  },
  {
    accessorKey: "automatic",
    header: ({ column }) => {
      return (
        <ButtonTable
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="w-full flex p-0 items-center justify-between hover:!bg-transparent hover:text-inherit text-foreground font-medium"
        >
          METHOD
          <TableSortingIcon />
        </ButtonTable>
      );
    },
    cell: ({ row }) => {
      return (
        <div className="text-foreground">
          <h3
            className={
              row.getValue("automatic")
                ? "text-notification-success"
                : "text-notification-warning"
            }
          >
            {row.getValue("automatic") ? "Automatic" : "Discount code"}
          </h3>
        </div>
      );
    },
  },
  {
    accessorKey: "type",
    header: ({ column }) => {
      return (
        <ButtonTable
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="w-full flex p-0 items-center justify-between hover:!bg-transparent hover:text-inherit text-foreground font-medium"
        >
          TYPE
          <TableSortingIcon />
        </ButtonTable>
      );
    },
    cell: ({ row }) => {
      const discountType = row.getValue("type");

      let discountTypeLabel = "";
      let discountScopeLabel = "";

      // TODO: add cases as per enum
      switch (discountType) {
        case "AmountOffOrder":
          discountTypeLabel = "Amount Off Order";
          discountScopeLabel = "Order discount";
          break;
        case "BuyXGetY":
          discountTypeLabel = "Buy X Get Y";
          discountScopeLabel = "Item discount";
          break;
        case "AmountOffProduct":
          discountTypeLabel = "Amount Off Product";
          discountScopeLabel = "Item discount";
          break;
        case "FreeShipping":
          discountTypeLabel = "Free Shipping";
          discountScopeLabel = "Order discount";
          break;
        default:
          discountTypeLabel = "Unknown";
          discountScopeLabel = "";
          break;
      }

      return (
        <div className="text-foreground">
          <h3 className="text-sm font-semibold">{discountTypeLabel}</h3>
          {discountScopeLabel && (
            <span className="text-xs text-muted-foreground">
              {discountScopeLabel}
            </span>
          )}
        </div>
      );
    },
  },
  {
    accessorKey: "combinations",
    header: ({ column }) => {
      return (
        <ButtonTable
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="w-full flex p-0 items-center justify-between hover:!bg-transparent hover:text-inherit text-foreground font-medium"
        >
          COMBINATIONS
          <TableSortingIcon />
        </ButtonTable>
      );
    },
    cell: ({ row }) => {
      return (
        <Badge
          variant="secondary"
          className="rounded-full px-2 py-1 bg-notification-warning/16 hover:bg-notification-warning/16 text-notification-warning border-0 text-xs whitespace-nowrap"
        >
          {row.getValue("combinations") || "Product Discount"}
        </Badge>
      );
    },
  },
  {
    accessorKey: "enabled",
    header: "STATUS",
    cell: ({ row }) => <CouponStatusCell coupon={row.original} />,
  },
  {
    accessorKey: "used",
    header: ({ column }) => {
      return (
        <ButtonTable
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="w-full flex p-0 items-center justify-between hover:!bg-transparent hover:text-inherit text-foreground font-medium"
        >
          USED
          <TableSortingIcon />
        </ButtonTable>
      );
    },
    cell: ({ row }) => {
      return (
        <span className="text-foreground text-sm">
          {row.getValue("used") || 0}
        </span>
      );
    },
  },
  {
    id: "actions",
    header: "ACTIONS",
    cell: ({ row }) => (
      <div className="flex items-center gap-2">
        <ButtonIcon
          onClick={() => onRowClick?.(row.original)}
          className="h-7 w-7 p-0 text-foreground hover:text-primary hover:bg-muted/30"
        >
          <Edit className="h-4 w-4" />
        </ButtonIcon>
        <ButtonIcon
          onClick={(e) => {
            e.stopPropagation();
            // TODO: Implement delete functionality
            console.log("Delete coupon:", row.original.id);
          }}
          className="h-7 w-7 p-0 text-foreground hover:text-destructive hover:bg-muted/30"
        >
          <Trash2 className="h-4 w-4" />
        </ButtonIcon>
      </div>
    ),
  },
];
